/* eslint-disable camelcase */
/* eslint-disable no-plusplus */
/**
 * @file AppointmentsCalendarView.jsx
 * @author Fernando Ruano
 * @description Appointments Calendar View
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Calendar, Select, Col, Row, Button, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/es';
import { groupBy } from 'lodash';

// Hooks
import usePatientIndicators from '@nephro/pages/Indicators/helpers/usePatientIndicators';

// Helpers
import { capitalizeFirstLetter } from '@nephro/commons/helpers/string-helper';
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';
import { STAGE_INDICATOR } from '@nephro/pages/Indicators/helpers/constants';
import { getPatientStage } from '@nephro/pages/Indicators/helpers/utils';

// Styles
import styles from './styles.module.css';

const AppointmentsCalendarView = ({ calendarActions }) => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { getAppointmentAvailabilities } = calendarActions;
  const [currentCalendarDate, setCurrentCalendarDate] = React.useState(
    moment()
  );
  const [attendaceSelection, setAttendaceSelection] = React.useState(1);
  const [selectedInfo, setSelectedInfo] = React.useState(null);
  const [quotesByMonth, setQuotesByMonth] = React.useState([]);
  const [allAppointments, setAllAppointments] = React.useState([]);
  const [stage] = usePatientIndicators(STAGE_INDICATOR);

  // Function to run action after set quotes info
  const afterSelect = (selectedQuotes) => {
    const quotesMonth =
      selectedQuotes &&
      moment(selectedQuotes[0].cap_fecha, 'YYYY-MM-DD').month();
    if (selectedQuotes && quotesMonth === currentCalendarDate.month()) {
      setSelectedInfo(selectedQuotes);
    } else {
      setSelectedInfo(null);
    }
  };

  // Function to set selected day quotes
  const onSelectDate = (value) => {
    const selectedQuotes =
      quotesByMonth[value.date()] === undefined
        ? false
        : quotesByMonth[value.date()];
    afterSelect(selectedQuotes);
  };

  // Function to get and set appointments availabilities list
  const onGetAppointmentAvailabilitiesList = (_, response) => {
    const noAvailaleMsg = 'No se encontro disponibilidad.';
    if (response?.data?.success && response?.data?.message !== noAvailaleMsg) {
      const { quotes } = response?.data?.data;
      const currentMonth = currentCalendarDate.month();
      const filterByMonth = quotes.filter(
        (itm) => moment(itm.cap_fecha).month() === currentMonth
      );
      const addDaytoObject = filterByMonth.map((itm) => {
        return {
          ...itm,
          day: moment(itm.cap_fecha).date()
        };
      });

      const groupByDays = groupBy(addDaytoObject, 'day');

      setAllAppointments(quotes);
      setQuotesByMonth(groupByDays);

      const selectedQuotes =
        groupByDays[currentCalendarDate.date()] === undefined
          ? false
          : groupByDays[currentCalendarDate.date()];
      afterSelect(selectedQuotes);
    } else {
      setAllAppointments([]);
    }
  };

  // Function to get quote type by patient stage
  const onGetQuoteType = (stageValue) => {
    switch (stageValue) {
      case '1':
        return 28; // E1
      case '2':
        return 29; // E2
      case '3a':
        return 30; // E3a
      case '3b':
        return 30; // E3b
      case '4':
        return 31; // E4
      default:
        return 32; // E5
    }
  };
  const getPatientInfo = async (attendaceType = null) => {
    const stageValue = await getPatientStage(stage.valor);

    const agendaMatePatientInfo = getStorageItem('agendaMatePatientInfo');

    if (agendaMatePatientInfo) {
      const data = {
        modalityId: attendaceType || attendaceSelection, // It must be (2) to on site
        patientCityId: agendaMatePatientInfo?.ciudad_id,
        patientId: agendaMatePatientInfo?.id, // It is a testing patient id
        quoteTypeId: onGetQuoteType(stageValue),
        desiredDate: `${moment(currentCalendarDate).format('YYYY-MM-DD')}`
      };
      getAppointmentAvailabilities(data, onGetAppointmentAvailabilitiesList);
    } else {
      Modal.error({
        onOk: () => history.goBack(),
        title: translate('Error al cargar las citas disponibles'),
        content:
          'Se ha presentado un error con el servicio de Agendamate, por favor inténtelo más tarde.'
      });
    }
  };

  React.useEffect(() => {
    if (stage) {
      getPatientInfo();
    }
  }, [currentCalendarDate, stage]);

  const onCancel = () => history.goBack();
  const onContinue = () =>
    history.push(
      `/main/appointments/${
        location.state ? 'reschedule-appointment' : 'schedule'
      }`,
      {
        selected: selectedInfo,
        toSchedule: location.state
      }
    );

  const onChangeMonth = (value) => {
    if (
      value.month() === currentCalendarDate.month() &&
      value.year() === currentCalendarDate.year()
    ) {
      onSelectDate(value);
    } else {
      const month = moment(value).format('MM');
      const year = moment(value).format('YYYY');
      setCurrentCalendarDate(moment(`01-${month}-${year}`, 'DD-MM-YYYY'));
    }
  };

  const onAttendanceTypeChange = async (attendanceType) => {
    setAttendaceSelection(attendanceType);
    await getPatientInfo(attendanceType);
  };

  return (
    <div className="h-full p-5">
      <h2 className="mb-0 text-base ml-2 text-baxterGray">
        {translate('calendarMg.selectDate')}
      </h2>
      <Calendar
        fullscreen={false}
        mode="month"
        onSelect={onChangeMonth}
        disabledDate={(currentDate) => {
          // Activate all available dates where appointments exist
          if (allAppointments.length) {
            const formatedCurrentDate = currentDate.format('YYYY-MM-DD');

            const availableDates = allAppointments.find(
              ({ cap_fecha }) => formatedCurrentDate === cap_fecha
            );

            return !availableDates;
          }
          return true;
        }}
        headerRender={({ value, onChange }) => {
          const attendanceHtmlOptions = [];
          const currentYear = moment().year();
          const currentMonth = moment().month();

          const end = 12;
          const attendanceData = [
            {
              name: 'Presencial',
              value: 1
            },
            {
              name: 'Virtual',
              value: 2
            }
          ];
          const monthOptions = [];
          const month = value.month();
          const year = value.year();
          const current = value.clone();
          const localeData = value.localeData();
          const start = year === currentYear ? currentMonth : 0;

          const months = [];
          for (let i = 0; i < 12; i++) {
            current.month(i);
            months.push(localeData.months(current));
          }
          for (let index = start; index < end; index++) {
            monthOptions.push(
              <Select.Option className="month-item" key={`${index}`}>
                {capitalizeFirstLetter(months[index])}
              </Select.Option>
            );
          }

          const options = [
            <Select.Option
              key={currentYear}
              value={currentYear}
              className="year-item">
              {currentYear}
            </Select.Option>,
            <Select.Option
              key={currentYear + 1}
              value={currentYear + 1}
              className="year-item">
              {currentYear + 1}
            </Select.Option>
          ];

          attendanceData.forEach((attendaceElement) => {
            attendanceHtmlOptions.push(
              <Select.Option
                key={attendaceElement?.value}
                value={attendaceElement?.value}
                className="year-item">
                {attendaceElement?.name}
              </Select.Option>
            );
          });

          return (
            <div style={{ padding: 8 }}>
              <Row gutter={10}>
                <Col xs={12} sm={6} md={6} lg={4} xl={4}>
                  <Select
                    dropdownMatchSelectWidth={false}
                    className="w-full"
                    onChange={(attendanceType) => {
                      onAttendanceTypeChange(attendanceType);
                    }}
                    value={attendaceSelection}>
                    {attendanceHtmlOptions}
                  </Select>
                </Col>
                <Col xs={12} sm={6} md={3} lg={3} xl={3}>
                  <Select
                    dropdownMatchSelectWidth={false}
                    className="w-full"
                    onChange={(newYear) => {
                      const now = value.clone().year(newYear);
                      onChange(now);
                      onChangeMonth(now);
                    }}
                    value={String(year)}>
                    {options}
                  </Select>
                </Col>
                <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                  <Select
                    className="w-full"
                    dropdownMatchSelectWidth={false}
                    value={String(month)}
                    onChange={(selectedMonth) => {
                      const newValue = value.clone();
                      newValue.month(parseInt(selectedMonth, 10));
                      onChange(newValue);
                      onChangeMonth(newValue);
                    }}>
                    {monthOptions}
                  </Select>
                </Col>
              </Row>
            </div>
          );
        }}
      />
      <div className="flex flex-row justify-between md:justify-end mt-7">
        <Button onClick={onCancel} type="link" className={styles.stepTwoButton}>
          {translate('commons.cancel')}
        </Button>
        <Button
          disabled={!selectedInfo}
          onClick={onContinue}
          className={[
            styles.stepTwoButton,
            `${!selectedInfo && 'opacity-40'}`
          ].join(' ')}
          type="primary">
          {translate('registration.continue')}
        </Button>
      </div>
    </div>
  );
};

/**
 * Component definition of proptypes.
 */
AppointmentsCalendarView.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  calendarActions: PropTypes.shape({
    getAppointmentAvailabilities: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool
};

export default AppointmentsCalendarView;
